import React, { useEffect, useState } from 'react';
import { Button, Table, Tag, message } from 'antd';
import { deleteSyncProfile, listSyncProfiles, runSyncProfileNow } from '../../services/customerImportApi';

// Story 2.2: lists saved sync profiles with a "Run now" / delete action per
// row. No edit-in-place for v1 -- delete and recreate via CustomerDataImport's
// "Save as Sync Profile" form (see the story's Dev Notes / Open Questions).
export default function SyncProfilesPanel() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [runningId, setRunningId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await listSyncProfiles();
      setProfiles(data || []);
    } catch (err) {
      message.error(err.message || 'Could not load sync profiles.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRunNow = async (profileId) => {
    setRunningId(profileId);
    try {
      const result = await runSyncProfileNow(profileId);
      if (result.status === 'ok') {
        message.success('Sync completed.');
      } else {
        message.error(result.summary?.error || 'Sync failed.');
      }
      await load();
    } catch (err) {
      message.error(err.message || 'Could not run sync.');
    } finally {
      setRunningId(null);
    }
  };

  const handleDelete = async (profileId) => {
    try {
      await deleteSyncProfile(profileId);
      message.success('Sync profile deleted.');
      await load();
    } catch (err) {
      message.error(err.message || 'Could not delete sync profile.');
    }
  };

  const columns = [
    { title: 'Source URL', dataIndex: 'source_url', ellipsis: true },
    {
      title: 'Interval',
      dataIndex: 'interval_minutes',
      render: (v) => (v ? `Every ${v} min` : '—'),
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      render: (v) => (v ? <Tag color="green">Enabled</Tag> : <Tag>Disabled</Tag>),
    },
    {
      title: 'Last Run',
      dataIndex: 'last_run_at',
      render: (v, record) => (v ? `${record.last_run_status} · ${v}` : 'Never run'),
    },
    {
      title: '',
      key: 'actions',
      render: (_, record) => (
        <div className="d-flex gap-2">
          <Button size="small" loading={runningId === record.id} onClick={() => handleRunNow(record.id)}>
            Run now
          </Button>
          <Button size="small" danger onClick={() => handleDelete(record.id)}>
            Delete
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Table
      rowKey="id"
      size="small"
      loading={loading}
      dataSource={profiles}
      columns={columns}
      pagination={false}
      locale={{ emptyText: 'No sync profiles yet.' }}
    />
  );
}
